import React, {useRef, useState} from 'react';
import {FilesNavigation} from '../FilesNavigation';
import {API_URL} from '../../constants';
import axios, {AxiosResponse} from 'axios';
import {authHeader, download} from '../../Utilities';
import {Button, Col, Input, message, Modal, Row} from 'antd';
import {axiosUploadRequest} from '../../UtilitiesTs';
import {RcFile} from 'antd/lib/upload';
import {useFetch} from "../../hooks/useFetch";

interface FileItem {
  name: string;
  size: number
  type: string
  uploadDate: string
}

interface ConfigureFilesProps {
  workspaceId: string;
  bucketName: string;
}

export function ConfigureFiles({ workspaceId, bucketName }: ConfigureFilesProps) {
  const [filesList, setFilesList] = useState<FileItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<File | boolean>(false);
  const filesInput = useRef<HTMLInputElement>(null);

  const [fileNameInput, setFileNameInput] = useState<string>('');

  useFetch(
      async () => {
        setIsLoading(true);
        try {
          const res = await axios.get<FileItem[]>(
              `${API_URL}/workspaces/${workspaceId}/files/${bucketName}`,
              {headers: authHeader()}
          );
          return res.data;
        } finally {
          setIsLoading(false);
        }
      },
      setFilesList,
      () => message.error("Can't fetch files"),
      [workspaceId]
  );

  const downloadFileHandler = (item: FileItem) => {
    const url = `${API_URL}/workspaces/${workspaceId}/files/${bucketName}/${item.name}`;
    const config = { headers: authHeader(), responseType: 'blob' as const };
    axios.get(url, config)
      .then((response: AxiosResponse<Blob>) => {
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        download(blob, item.name);
      })
      .catch(() => message.error(`Can't download the "${item.name}"`));
  };

  const deleteFileHandler = (item: FileItem) => {
    const url = `${API_URL}/workspaces/${workspaceId}/files/${bucketName}/${item.name}`;
    const config = { headers: authHeader() };
    axios.delete(url, config)
      .then(() => {
        setFilesList((prev) => prev.filter(({ name }) => name !== item.name))
        message.success(`The "${item.name}" is deleted`)
      })
      .catch(() => message.error(`Can't delete the "${item.name}"`));
  };

  const handleClickUploadFile = () => {
    if (filesInput.current) {
      filesInput.current.click();
    }
  };

  const handleChangeFileInput = (file: RcFile, replace = false) => {
    const fileExists = filesList.filter(({ name }) => name === file.name);
    if (fileExists.length && !replace) {
      setFileNameInput(file.name);
      setShowModal(file);
      return;
    }

    setShowModal(false);
    axiosUploadRequest({
      file: file,
      action: `${API_URL}/workspaces/${workspaceId}/files/${bucketName}`,
      headers: { headers: authHeader() },
      onSuccess: (response: object) => {
        const fileResponse = response as FileItem;
        if (fileExists.length) {
          setFilesList(prev => prev.map((file) => {
            if (file.name == fileResponse.name) {
              return fileResponse
            }
            return file
          }))
        } else {
          setFilesList(prev => [...prev, fileResponse])
        }
        setIsLoading(false)
        message.success(`The "${fileResponse.name}" is uploaded`)
      },
      onProgress: () => {
        if (!isLoading) {setIsLoading(true)}
      },
      onError: () => {
        message.error(`Can't upload the "${file.name}"`)
        setIsLoading(false)
      },
      data: {},
      filename: '',
      withCredentials: false
    });
  };

  const RenameNewFile = () => {
    if (!filesInput.current?.files?.[0]) return;

    const file = filesInput.current.files[0];
    const extension = file.name.split('.').at(-1);

    let newFileName = fileNameInput;
    if (extension && !fileNameInput.endsWith(`.${extension}`)) {
      newFileName = `${fileNameInput}.${extension}`;
    }

    const newFile = new File([file], newFileName, {
      type: file.type,
      lastModified: file.lastModified,
    }) as RcFile;

    setShowModal(false);
    handleChangeFileInput(newFile);
  };

  return (
    <div>
      <Button onClick={handleClickUploadFile} type="primary" style={{ marginRight: "10px" }}>
        Upload file(s)
      </Button>
      <input
        type="file"
        style={{ display: "none" }}
        ref={filesInput}
        onChange={() => {
          if (filesInput.current?.files?.[0]) {
            handleChangeFileInput(filesInput.current.files[0] as RcFile);
          }
        }}
      />
      <FilesNavigation
        data={filesList}
        folder={""}
        isLoading={isLoading}
        tableProps={{}}
        tableColumnsProps={{}}
        hideSearchField={true}
        hideDowloadFolderButton={true}
        hidePathBreadcrumbs={true}
        onDownloadFile={downloadFileHandler}
        onDeleteFile={deleteFileHandler}
      />
      <Modal
        title="File renaming or replacing"
        visible={!!showModal}
        onCancel={() => setShowModal(false)}
        footer={null}
      >
        <Col>
          <Row>
            <Input
              value={fileNameInput}
              onChange={(e) => setFileNameInput(e.target.value)}
              style={{ width: '70%' }}
            />
            <Button onClick={RenameNewFile} type="primary" style={{ marginLeft: '10px' }}>
              Rename
            </Button>
          </Row>
          <Row>
            <Button
              onClick={() => {
                if (filesInput.current?.files?.[0]) {
                  handleChangeFileInput(filesInput.current.files[0] as RcFile, true);
                }
              }}
              type="primary"
              style={{ marginTop: '10px' }}
            >
              Replace
            </Button>
          </Row>
        </Col>
      </Modal>
    </div>
  );
}